/**
 * ManualPricingModal — sub-modal for setting an arbitrary (manual) price on a single order line.
 *
 * @see AC-2, AC-3, AC-4 in docs/specifications/2026-05-12-admin-manual-pricing-ui/spec.md
 */
import React, { useEffect, useRef, useState, useId } from "react";
import { ARBITRARY_PRICE_VALIDATION } from "../../../../constants/billing-validation";
import { useSetArbitraryPriceMutation } from "../../../../hooks/useAdminProductCatalog";
import {
  getPricingLabel,
  type OrderLinePricingType,
} from "../../../../types/billing-labels";
import { formatCentsWithDecimals } from "../utils/orderFormatters";

// Format helpers

/** Polish VAT rate applied to gross-preview calculation. */
const VAT_RATE = 0.23;

const formatGross = (netPLN: number): string => {
  if (netPLN === 0) return "Brutto: 0 PLN (bezpłatne)";
  const gross = netPLN * (1 + VAT_RATE);
  const formatted = new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    currencyDisplay: "code",
    maximumFractionDigits: 0,
  }).format(gross);
  return `Brutto: ${formatted} (VAT ${(VAT_RATE * 100).toFixed(0)}%)`;
};

// Props

export interface ManualPricingModalProps {
  orderId: string;
  brandId: string;
  lineId: string;
  /** Current pricing info — used to show "current price" context when already priced */
  currentPricing?: {
    type: OrderLinePricingType;
    netCents: number | null;
  } | null;
  onClose: () => void;
  /** Ref to the trigger button — focus is returned here on close */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}

// Component

export const ManualPricingModal: React.FC<ManualPricingModalProps> = ({
  orderId,
  brandId,
  lineId,
  currentPricing,
  onClose,
  triggerRef,
}) => {
  const titleId = useId();
  const [netPLN, setNetPLN] = useState<number>(0);
  const [arbitraryReason, setArbitraryReason] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const netInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const mutation = useSetArbitraryPriceMutation(orderId, brandId);

  const { MIN_REASON_LENGTH, MAX_REASON_LENGTH, WARN_REASON_LENGTH, MIN_NET_PLN } =
    ARBITRARY_PRICE_VALIDATION;

  const isReasonTooShort = arbitraryReason.length < MIN_REASON_LENGTH;
  const isNetNegative = netPLN < MIN_NET_PLN;
  const isSubmitDisabled = isReasonTooShort || isNetNegative || mutation.isPending;

  // Character counter styling
  const charCountColor =
    arbitraryReason.length >= MAX_REASON_LENGTH
      ? "text-red-600"
      : arbitraryReason.length >= WARN_REASON_LENGTH
      ? "text-amber-600"
      : "text-neutral-400";

  // Stable ref to onClose/triggerRef so the Esc effect can run once
  // (avoids stale-closure risk without a useCallback contract on the parent).
  const onCloseRef = useRef(onClose);
  const triggerRefRef = useRef(triggerRef);
  useEffect(() => {
    onCloseRef.current = onClose;
    triggerRefRef.current = triggerRef;
  }, [onClose, triggerRef]);

  const handleClose = () => {
    onCloseRef.current();
    // Return focus to trigger button
    setTimeout(() => {
      triggerRefRef.current?.current?.focus();
    }, 0);
  };

  // Auto-focus net input on open
  useEffect(() => {
    netInputRef.current?.focus();
  }, []);

  // Esc closes modal (stops propagation so outer modal stays open).
  // Empty deps are safe — handleClose reads onClose/triggerRef via refs.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        setTimeout(() => {
          triggerRefRef.current?.current?.focus();
        }, 0);
      }
    };
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, []);

  // Focus trap
  const handleTabTrap = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const modal = modalRef.current;
    if (!modal) return;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    setValidationError(null);

    if (isReasonTooShort) {
      setValidationError(`Minimum ${MIN_REASON_LENGTH} znaków`);
      return;
    }
    if (isNetNegative) {
      setValidationError("Cena nie może być ujemna");
      return;
    }

    // Floating-point guard: `toFixed(2)` first snaps the value to 2 decimal
    // places before the `* 100` multiplication. Without it, cases like
    // `0.1 * 100 = 10.000…002` or `1500.005 * 100 = 150000.4999…` would
    // round to the wrong integer (and the backend `@IsInt()` rejects floats).
    const netCents = Math.round(parseFloat(netPLN.toFixed(2)) * 100);

    try {
      await mutation.mutateAsync({ lineId, netCents, arbitraryReason });
      handleClose();
    } catch {
      // Error toast already shown by mutation hook; modal stays open
    }
  };

  const showCurrentPrice =
    currentPricing &&
    currentPricing.type !== "NOT_PRICED_YET" &&
    currentPricing.netCents != null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleTabTrap}
        className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2
            id={titleId}
            className="text-base font-semibold text-neutral-900"
          >
            Wyceń ręcznie
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Net price input */}
          <div>
            <label
              htmlFor="manual-pricing-net"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Cena netto (PLN)
            </label>
            <input
              ref={netInputRef}
              id="manual-pricing-net"
              type="number"
              min={MIN_NET_PLN}
              step={0.01}
              value={netPLN}
              disabled={mutation.isPending}
              onChange={(e) => setNetPLN(parseFloat(e.target.value) || 0)}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
            />
          </div>

          {/* Current price context (shown when line already priced) */}
          {showCurrentPrice && (
            <p className="text-xs text-neutral-500">
              Aktualna cena:{" "}
              <span className="font-medium">
                {formatCentsWithDecimals(currentPricing!.netCents!)}
              </span>{" "}
              (typ: {getPricingLabel(currentPricing!.type)})
            </p>
          )}

          {/* Gross preview */}
          <p
            aria-live="polite"
            aria-atomic="true"
            className="text-xs text-neutral-600 font-medium"
          >
            {formatGross(isNetNegative ? 0 : netPLN)}
          </p>

          {/* Reason textarea */}
          <div>
            <label
              htmlFor="manual-pricing-reason"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Powód
            </label>
            <textarea
              id="manual-pricing-reason"
              rows={4}
              maxLength={MAX_REASON_LENGTH}
              value={arbitraryReason}
              disabled={mutation.isPending}
              onChange={(e) => setArbitraryReason(e.target.value)}
              placeholder={`Podaj powód ręcznej wyceny (min. ${MIN_REASON_LENGTH} znaków)`}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 resize-none"
            />
            <p className={`text-xs mt-0.5 text-right ${charCountColor}`}>
              {arbitraryReason.length} / {MAX_REASON_LENGTH}
            </p>
            {/* Inline validation */}
            {arbitraryReason.length > 0 && isReasonTooShort && (
              <p
                role="alert"
                aria-live="polite"
                className="text-xs text-red-600 mt-1"
              >
                Minimum {MIN_REASON_LENGTH} znaków
              </p>
            )}
          </div>

          {/* Generic validation error */}
          {validationError && (
            <p
              role="alert"
              aria-live="polite"
              className="text-xs text-red-600"
            >
              {validationError}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {mutation.isPending && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" aria-hidden="true" />
            )}
            {mutation.isPending ? "Zapisuję..." : "Zapisz"}
          </button>
        </div>
      </div>
    </div>
  );
};
